import { useState, useEffect } from 'react';
import useDebouncedValue, { SEARCH_DEBOUNCE_MS } from './useDebouncedValue';
import { validateSearchTerm, serverSearchTerm } from '../utils/searchTerm';

/**
 * The server half of a "name or rego" box: what to send, and what to say if it cannot
 * be sent.
 *
 * <p>Takes the text the user is typing — wherever it is kept — and gives back the
 * settled, validated term for the wire. Every list that carries such a box runs at two
 * speeds, and this is the slower one:
 *
 * <ul>
 *   <li><b>The text narrows the loaded page NOW</b>, through
 *       {@code filterByNameOrRego}. Twenty cards filter faster than a keystroke.</li>
 *   <li><b>`q` asks the SERVER once the typing settles.</b> The page holds twenty of
 *       possibly thousands, so the answer for "Smith" is usually not on it. The server
 *       decides whether the text is a rego or a name and searches the right column.</li>
 *   <li><b>`error` is the sentence under the box</b> — the same rule the server
 *       enforces, checked here so a refused keystroke never costs a round trip.</li>
 * </ul>
 *
 * <p>🔑 An invalid term leaves the last valid search STANDING. Clearing `q` on a typo
 * would reload the whole lane behind an error message — the list would change under a
 * box the user is being told is wrong, and one backspace would change it back.
 *
 * <p>Use this directly where the text already lives somewhere else (the split view's
 * box belongs to the toolbar); use {@link useSearchBox} where the box owns it.
 *
 * @param {string} text
 * @returns {{ q: string|null, error: string|null }}
 */
export function useServerSearchTerm(text) {
    const { ok, message } = validateSearchTerm(text);

    // Only a valid term is ever a candidate for the wire; see the note above.
    const [valid, setValid] = useState(null);
    useEffect(() => {
        if (ok) setValid(serverSearchTerm(text));
    }, [text, ok]);

    return { q: useDebouncedValue(valid, SEARCH_DEBOUNCE_MS), error: message };
}

/**
 * A search box that owns its own text, plus everything {@link useServerSearchTerm}
 * gives back. What the Queued lane, the phone's job list and the quotes lane each use.
 *
 * @returns {{ text: string, setText: (v: string) => void, error: string|null,
 *             q: string|null, clear: () => void }}
 */
export default function useSearchBox() {
    const [text, setText] = useState('');
    const { q, error } = useServerSearchTerm(text);

    return { text, setText, error, q, clear: () => setText('') };
}
